/** A LinkedIn profile URL the user typed, ready to be offered as a selection. */
export interface ParsedLinkedinProfileUrl {
	/** Canonical form of the URL, so what we store never depends on how it was pasted. */
	url: string;
	/** Best-effort display name for the chip, derived from the profile slug. */
	name: string;
}

/** Matches the profile slug of any LinkedIn host ("de.linkedin.com/in/max-1a2b" → "max-1a2b"). */
const PROFILE_URL_PATTERN = /linkedin\.com\/in\/([^/?#\s]+)/i;

const decodeSlug = (slug: string): string => {
	try {
		return decodeURIComponent(slug);
	} catch {
		return slug;
	}
};

const capitalize = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * Turns a profile slug into a readable name, dropping the disambiguation suffix
 * LinkedIn appends ("robert-vossen-1a2b3c4" → "Robert Vossen").
 */
const displayNameFromSlug = (slug: string): string => {
	const parts = decodeSlug(slug).split("-").filter(Boolean);
	const last = parts[parts.length - 1];
	const words = parts.length > 1 && last && /\d/.test(last) ? parts.slice(0, -1) : parts;

	return words.map(capitalize).join(" ");
};

/**
 * Recognises a LinkedIn profile URL typed into the profile typeahead, so the one
 * field accepts both a name to search for and a URL to use as-is (PREC-3719).
 * Returns `null` for anything that is not a profile URL — i.e. a search query.
 */
export const parseLinkedinProfileUrl = (input: string): ParsedLinkedinProfileUrl | null => {
	const slug = PROFILE_URL_PATTERN.exec(input.trim())?.[1];

	if (!slug) {
		return null;
	}

	return {
		url: `https://www.linkedin.com/in/${slug}`,
		name: displayNameFromSlug(slug) || slug,
	};
};
